import { ChangeDetectorRef, Directive, ElementRef, Inject, Input, NgModule, OnDestroy, OnInit, Renderer2, OnChanges, SimpleChanges } from '@angular/core';
import { WINDOW } from '@app/common/app-tokens';
import { ImageService } from '@app/services/image.service';
import { of, Subscription } from 'rxjs';
import { mergeMap } from 'rxjs/operators';

@Directive({
  selector: '[appCssBackgroundImageProxy]'
})
export class CssBackgroundImageProxyDirective implements OnInit, OnDestroy, OnChanges {
  subscriptions = new Subscription();
  @Input('appCssBackgroundImageProxy') originalUrl: string;

  constructor(private el: ElementRef,
              private imageService: ImageService,
              @Inject(WINDOW) private window: Window,
              private cdRef: ChangeDetectorRef,
              private renderer2: Renderer2) { }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.originalUrl.currentValue !== changes.originalUrl.previousValue && !changes.originalUrl.isFirstChange()) {
      this.setBackgroundImageOfCurrentElement(changes.originalUrl.currentValue);
    }
  }

  ngOnInit(): void {
    this.setBackgroundImageOfCurrentElement(this.originalUrl);
  }

  private setBackgroundImageOfCurrentElement(url: string) {
    if (url) {

      if (url.startsWith('data:') || url.indexOf('sharepoint.com') === -1) {
        // already in correct format
        this.renderer2.setStyle(this.el.nativeElement, 'background-image', `url("${url}")`);
        this.cdRef.detectChanges();
      } else {
        if (url.startsWith('!')) {
          // get original image
          const replacedUrl = url.replace('!', '');

          if (url.toLowerCase().indexOf('/_api/web/lists'.toLowerCase()) !== -1) {
            this.subscriptions.add(this.imageService
              .getPublishingImageUrl(replacedUrl)
              .pipe(
                mergeMap(result => {
                  if (result) {
                    if (result.startsWith('data:')) {
                      return of(result);
                    } else {
                      return this.imageService.getSharePointImageThumbnailFromOriginalUrlWithStandardDimensions(result);
                    }
                  } else {
                    return of(null);
                  }
                }))
              .subscribe(result => {
                if (result) {
                  this.renderer2.setStyle(this.el.nativeElement, 'background-image', `url("${result}")`);
                }
                this.cdRef.detectChanges();
              }));
          } else {
            this.subscriptions.add(this.imageService
              .getSharePointImageThumbnailFromOriginalUrlWithStandardDimensions(replacedUrl)
              .subscribe(result => {
                if (result) {
                  this.renderer2.setStyle(this.el.nativeElement, 'background-image', `url("${result}")`);
                }
                this.cdRef.detectChanges();
              }));
          }
        } else {
          // generate thumnail
          // setTimeout: Need to wait to let browser draw flexboxes so we can measure them and figure out what image rendition to use
          this.window.setTimeout(() => {

            let w = this.el.nativeElement.clientWidth;
            if (w > 960 && w < 1400) {
              w = 960; // 960 seems like the most common "large" image size so we "lock it" between these sizes
            }
            w = Math.max(w, 576); // 576 is the smallest boostrap breakpoint, where layout turns fluid
            const h = 99999; // using 99999 to get the thumbnail generation to keep aspect ratio based on width. (this.el.nativeElement.clientHeight)

            if (url.toLowerCase().indexOf('/_api/web/lists'.toLowerCase()) !== -1) {
              this.subscriptions.add(this.imageService
                .getPublishingImageUrl(url)
                .pipe(
                  mergeMap(result => {
                    if (result) {
                      if (result.startsWith('data:')) {
                        return of(result);
                      } else {
                        return this.imageService.getSharePointImageThumbnailFromOriginalUrlWithCustomDimensions(result, w, h);
                      }
                    } else {
                      return of(null);
                    }
                  }))
                .subscribe(result => {
                  if (result) {
                    this.renderer2.setStyle(this.el.nativeElement, 'background-image', `url("${result}")`);
                  }
                  this.cdRef.detectChanges();
                }));
            } else {
              this.subscriptions.add(this.imageService
                .getSharePointImageThumbnailFromOriginalUrlWithCustomDimensions(url, w, h)
                .subscribe(result => {
                  if (result) {
                    this.renderer2.setStyle(this.el.nativeElement, 'background-image', `url("${result}")`);
                  }
                  this.cdRef.detectChanges();
                }));
            }
          }, 1);
        }
      }
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
@NgModule({
  declarations: [CssBackgroundImageProxyDirective],
  exports: [CssBackgroundImageProxyDirective],
})
export class CssBackgroundImageProxyModule { }
